<template>
    <nav id="ob-rail" ref="railEl" aria-label="Page sections" :class="{ 'ob-corp-rail': overCorpBand }">
        <div class="rail-flex">
            <div class="rail-track">
                <div id="ob-rail-progress" :style="{ height: progress + '%' }" />
            </div>
            <div id="ob-rail-items">
                <button
                    v-for="r in RAIL"
                    :key="r.id"
                    class="ob-rail-item"
                    :aria-current="active === r.id ? 'true' : 'false'"
                    @click="jump(r.id)"
                >
                    <span class="ob-rail-tick" />
                    <span class="ob-rail-label">{{ r.label }}</span>
                </button>
            </div>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import { onBeforeUnmount, onMounted, ref } from 'vue'

    import { RAIL } from '@/data/content'
    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()
    const railEl = ref<HTMLElement | null>(null)
    const progress = ref(0)
    const active = ref(RAIL[0]?.id ?? 'top')
    const overCorpBand = ref(false)

    const jump = (id: string) => {
        const el = document.getElementById(id)
        if (el) window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - 96), behavior: 'smooth' })
    }

    let raf = 0
    const sync = () => {
        if (raf) return
        raf = requestAnimationFrame(() => {
            raf = 0
            const max = (document.documentElement.scrollHeight - window.innerHeight) || 1
            progress.value = Math.min(100, Math.max(0, (window.scrollY / max) * 100))

            // active = last section whose top has passed the upper third of the viewport
            const line = window.innerHeight * 0.34
            let a = RAIL[0]?.id ?? 'top'
            RAIL.forEach((r) => {
                const el = document.getElementById(r.id)
                if (el && el.getBoundingClientRect().top <= line) a = r.id
            })
            if (window.scrollY >= max - 4) a = RAIL[RAIL.length - 1]?.id ?? a
            active.value = a

            // the rail sits over whichever band is behind it
            const band = document.getElementById('offer')
            if (railEl.value && band) {
                const r = railEl.value.getBoundingClientRect()
                const b = band.getBoundingClientRect()
                overCorpBand.value = store.isCorp && b.top < r.bottom && b.bottom > r.top
            } else {
                overCorpBand.value = false
            }
        })
    }

    onMounted(() => {
        window.addEventListener('scroll', sync, { passive: true })
        window.addEventListener('resize', sync)
        sync()
        setTimeout(sync, 600)
    })
    onBeforeUnmount(() => {
        window.removeEventListener('scroll', sync)
        window.removeEventListener('resize', sync)
        if (raf) cancelAnimationFrame(raf)
    })

    defineExpose({ sync })
</script>

<style scoped>
    .rail-flex { display: flex; align-items: stretch; gap: 14px; }
    .rail-track { position: relative; width: 2px; border-radius: 2px; background: var(--ob-navy-a12); flex: none; }
    #ob-rail-progress {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 0%;
        border-radius: 2px;
        background: var(--ob-grad-rail);
        transition: height .18s linear;
    }
    #ob-rail-items { display: flex; flex-direction: column; }
</style>
